<template>
	<v-dialog
		ref="dialog"
		v-model="isOpen"
		:attach="attach"
		:max-width="maxWidth"
		:max-height="maxHeight"
		:persistent="persistent"
		:scrollable="scrollable"
		:fullscreen="fullscreen"
		:hide-overlay="hideOverlay"
		:content-class="mergedContentClass"
		:dark="isDark"
		:light="light"
		:transition="transition"
		:overlay-opacity="overlayOpacity"
		:eager="eager"
		:retain-focus="retainFocus"
		v-bind="$attrs"
		v-on="forwardedListeners"
		@click:outside="$emit('click:outside', $event)"
	>
		<template v-slot:activator="{ on, attrs }">
			<slot
				name="activator"
				v-bind="{ on, attrs }"
			/>
		</template>
		<slot />
	</v-dialog>
</template>

<script>
import { accessible_modal_mixin } from '../../mixins/accessible_modal_mixin'

let dialogContentId = 0

export default {
	name: 'KDialog',
	mixins: [accessible_modal_mixin],
	props: {
		value: {
			type: null,		// any type is acceptable here
			required: true
		},
		attach: {
			type: [String, Boolean, Object],
			default: undefined
		},
		maxWidth: {
			type: [String, Number],
			default: undefined
		},
		maxHeight: {
			type: [String, Number],
			default: undefined
		},
		persistent: {
			type: Boolean,
			default: false
		},
		scrollable: {
			type: Boolean,
			default: false
		},
		fullscreen: {
			type: Boolean,
			default: false
		},
		hideOverlay: {
			type: Boolean,
			default: false
		},
		contentClass: {
			type: String,
			default: undefined
		},
		transition: {
			type: [String, Boolean],
			default: undefined
		},
		overlayOpacity: {
			type: [Number, String],
			default: undefined
		},
		eager: {
			type: Boolean,
			default: false
		},
		light: {
			type: Boolean,
			default: false
		},
		retainFocus: {
			type: Boolean,
			default: true
		},
		theme: {
			type: String,
			default: 'light'
		},
		role: {
			type: String,
			default: 'dialog'
		},
		ariaModal: {
			type: [Boolean, String],
			default: true
		},
		ariaLabelledby: {
			type: String,
			default: undefined
		},
		ariaLabel: {
			type: String,
			default: undefined
		},
		restoreFocus: {
			type: Boolean,
			default: true
		},
		restoreFocusElement: {
			type: null,
			default: undefined
		},
		autoFocus: {
			type: Boolean,
			default: true
		},
		initialFocusSelector: {
			type: String,
			default: undefined
		}
	},
	data() {
		return {
			contentClassName: `k-dialog-content-${dialogContentId++}`
		}
	},
	computed: {
		isDark() {
			return this.theme === 'dark';
		},
		mergedContentClass() {
			return [this.contentClass, this.contentClassName].filter(Boolean).join(' ')
		},
		forwardedListeners() {
			const { keydown, 'click:outside': clickOutside, ...rest } = this.$listeners
			return rest
		},
		isOpen: {
			get() {
				return this.value;
			},
			set(newValue) {
				this.$emit('input', newValue);
			}
		}
	},
	mounted() {
		this.$nextTick(() => this.syncDialogAccessibilityAttributes())
		if (!this.value) return
		this.capture_accessible_modal_focus_origin()
		this.register_accessible_modal()
		this.$nextTick(() => this.focus_accessible_modal_content())
	},
	beforeDestroy() {
		this.unregister_accessible_modal()
		this.restore_accessible_modal_focus_origin()
	},
	watch: {
		value(isOpen) {
			this.$nextTick(() => this.syncDialogAccessibilityAttributes())
			if (isOpen) {
				this.capture_accessible_modal_focus_origin()
				this.register_accessible_modal()
				this.$nextTick(() => this.focus_accessible_modal_content())
				return
			}
			this.unregister_accessible_modal()
			this.$nextTick(() => this.restore_accessible_modal_focus_origin())
		}
	},
	updated() {
		this.syncDialogAccessibilityAttributes()
	},
	methods: {
		accessible_modal_is_open() {
			return this.value
		},
		get_accessible_modal_focus_origin() {
			if (this.restoreFocusElement) return this.restoreFocusElement
			if (typeof document === 'undefined') return null
			const has_html_element = typeof HTMLElement !== 'undefined'
			return has_html_element && document.activeElement instanceof HTMLElement ? document.activeElement : null
		},
		getDialogRoot() {
			if (typeof document === 'undefined') return null
			return document.querySelector(`.${this.contentClassName}.v-dialog`) || null
		},
		syncDialogAccessibilityAttributes() {
			const dialogRoot = this.getDialogRoot()
			if (!dialogRoot) return

			dialogRoot.setAttribute('role', this.role)
			dialogRoot.setAttribute('aria-modal', String(this.ariaModal))
			dialogRoot.setAttribute('tabindex', '-1')

			if (this.ariaLabelledby) dialogRoot.setAttribute('aria-labelledby', this.ariaLabelledby)
			else dialogRoot.removeAttribute('aria-labelledby')

			if (this.ariaLabel) dialogRoot.setAttribute('aria-label', this.ariaLabel)
			else dialogRoot.removeAttribute('aria-label')
		},
		get_accessible_modal_container() {
			return this.getDialogRoot()
		},
		should_auto_focus_accessible_modal() {
			return this.autoFocus
		},
		should_restore_accessible_modal_focus() {
			return this.restoreFocus
		},
		should_trap_accessible_modal_focus() {
			return true
		},
		close_accessible_modal_from_escape(event) {
			this.$emit('keydown.esc', event)
			this.$emit('escape', event)
		}
	}
};
</script>
